/* Native scrolling and dialog semantics; no auto-rotation or photo manipulation. */
if (!customElements.get('nada-customer-gallery')) {
  customElements.define('nada-customer-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const track = this.querySelector('.nada-editorial__gallery');
      const previous = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      this.querySelector('.nada-editorial__gallery-controls').hidden = false;
      const update = () => {
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      };
      const move = direction => track.scrollBy({
        left: direction * (track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap)),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') track.scrollLeft = 0;
        else if (event.key === 'End') track.scrollLeft = track.scrollWidth;
        else move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  });
}

if (!customElements.get('nada-label-viewer')) {
  customElements.define('nada-label-viewer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const dialog = this.querySelector('dialog');
      if (!dialog.showModal) return; // Original-file link remains useful without dialog support.
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const opener = this.querySelector('[data-label-open]');
      const image = dialog.querySelector('img');
      const zoom = dialog.querySelector('[data-label-zoom]');
      const viewport = dialog.querySelector('.nada-label-dialog__viewport');
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.setAttribute('aria-controls', dialog.id);
      this.restore = () => {
        if (!this.lock) return;
        const { y, body, overflow } = this.lock;
        Object.assign(document.body.style, body);
        document.documentElement.style.overflow = overflow;
        this.lock = null;
        window.scrollTo({ top: y, behavior: 'instant' });
        if (opener.isConnected) opener.focus({ preventScroll: true });
      };
      opener.addEventListener('click', event => {
        event.preventDefault();
        if (dialog.open) return;
        const body = {};
        for (const property of ['position', 'top', 'width']) body[property] = document.body.style[property];
        this.lock = { y: window.scrollY, body, overflow: document.documentElement.style.overflow };
        document.documentElement.style.overflow = 'hidden';
        Object.assign(document.body.style, { position: 'fixed', top: `-${this.lock.y}px`, width: '100%' });
        if (!image.src) image.src = image.dataset.labelSource;
        dialog.classList.remove('is-zoomed');
        zoom.setAttribute('aria-pressed', 'false');
        zoom.textContent = zoom.dataset.zoomIn;
        dialog.showModal();
        viewport.scrollTo(0, 0);
      }, options);
      zoom.addEventListener('click', () => {
        const centerX = (viewport.scrollLeft + viewport.clientWidth / 2) / image.clientWidth;
        const centerY = (viewport.scrollTop + viewport.clientHeight / 2) / image.clientHeight;
        const zoomed = dialog.classList.toggle('is-zoomed');
        zoom.setAttribute('aria-pressed', String(zoomed));
        zoom.textContent = zoomed ? zoom.dataset.zoomOut : zoom.dataset.zoomIn;
        viewport.scrollTo(zoomed ? centerX * image.clientWidth - viewport.clientWidth / 2 : 0,
          zoomed ? centerY * image.clientHeight - viewport.clientHeight / 2 : 0);
      }, options);
      dialog.querySelector('[data-label-close]').addEventListener('click', () => dialog.close(), options);
      // Native Escape, focus containment and inert background come from showModal().
      dialog.addEventListener('close', this.restore, options);
    }
    disconnectedCallback() {
      this.querySelector('dialog')?.close();
      this.restore?.();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
